<script setup>
import {
  computed,
} from 'vue';
import GenericBadge from '@/components/generic/GenericBadge.vue';
import NavIcon from '@/components/NavIcon.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  label: {
    type: String,
    required: false,
  },
  rawLabel: {
    type: String,
    required: false,
  },
  icon: {
    type: String,
    required: false,
  },
  image: {
    // Should be in the assets/icons folder.
    type: String,
    required: false,
  },
  imageLight: {
    type: String,
    required: false,
  },
  imageDark: {
    type: String,
    required: false,
  },
  mediaImage: {
    type: String,
    required: false,
  },
  href: {
    type: String,
    default: null,
  },
  target: {
    type: String,
    default: '_self',
  },
  route: {
    type: String,
    default: null,
  },
  routeTo: {
    type: Object,
    default: null,
  },
  active: {
    type: Boolean,
    default: false,
  },
  badge: {
    type: String,
    default: null,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
  danger: {
    type: Boolean,
    default: false,
  },
  primaryLight: {
    type: Boolean,
    default: false,
  },
  showDot: {
    type: Boolean,
    default: false,
  },
  iconSize: {
    type: Number,
    default: 22,
  },
  iconCircle: {
    type: Boolean,
    default: false,
  },
  iconCircleColor: {
    type: String,
    default: 'transparent',
  },
  subItem: {
    type: Boolean,
    default: false,
  },
  // A padlock after the label for a screen that is there but not active for
  // the user. The item stays a link: the screen itself explains why.
  locked: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const tooltipConfig = computed(() => {
  if (!props.collapsed) {
    return null;
  }
  const label = props.label ? t(props.label) : props.rawLabel;
  if (!label) {
    return null;
  }
  return { label, placement: 'right' };
});
</script>

<template>
  <li class="nav-item" :class="{ 'nav-item-sub': subItem && !collapsed }">
    <a
      v-if="href"
      :href="href"
      class="nav-link"
      :class="{'active': active, 'nav-link-danger': danger, 'nav-link-primary-light': primaryLight, 'is-collapsed': collapsed, 'nav-link-sub': subItem && !collapsed}"
      :target="target"
      v-tooltip="tooltipConfig"
    >
      <nav-icon
        :icon="icon"
        :image="image"
        :image-light="imageLight"
        :image-dark="imageDark"
        :media-image="mediaImage"
        :active="active"
        :size="iconSize"
        :circle="iconCircle"
        :circle-color="iconCircleColor"
      />
      <span
        class="nav-link-text truncate-text ms-1 ps-1"
        v-if="label && !collapsed"
      >{{ $t(label) }}</span>
      <span
        class="nav-link-text truncate-text ms-1 ps-1"
        v-else-if="rawLabel && !collapsed"
      >{{ rawLabel }}</span>
      <generic-badge
        v-if="badge && !collapsed"
        small
        :label="badge"
      />
      <span
        v-if="showDot && !collapsed"
        class="nav-dot attention-dot"
      />
      <i
        v-if="locked && !collapsed"
        class="fa fa-lock nav-link-lock"
        role="img"
        :aria-label="$t('phrase.not_available')"
      />
    </a>
    <router-link
      v-else-if="routeTo || route"
      class="nav-link"
      :class="{'active': active, 'nav-link-danger': danger, 'nav-link-primary-light': primaryLight, 'is-collapsed': collapsed, 'nav-link-sub': subItem && !collapsed}"
      :to="routeTo || {name: route}"
      v-tooltip="tooltipConfig"
    >
      <nav-icon
        :icon="icon"
        :image="image"
        :image-light="imageLight"
        :image-dark="imageDark"
        :media-image="mediaImage"
        :active="active"
        :size="iconSize"
        :circle="iconCircle"
        :circle-color="iconCircleColor"
      />
      <span
        class="nav-link-text truncate-text ms-1 ps-1"
        v-if="label && !collapsed"
      >{{ $t(label) }}</span>
      <span
        class="nav-link-text truncate-text ms-1 ps-1"
        v-else-if="rawLabel && !collapsed"
      >{{ rawLabel }}</span>

      <generic-badge
        v-if="badge && !collapsed"
        small
        :label="badge"
      />
      <span
        v-if="showDot && !collapsed"
        class="nav-dot attention-dot"
      />
      <i
        v-if="locked && !collapsed"
        class="fa fa-lock nav-link-lock"
        role="img"
        :aria-label="$t('phrase.not_available')"
      />
    </router-link>
    <div v-else class="text-danger">
      Route or href is required.
    </div>
  </li>
</template>

<style scoped>
.nav-link.active span {
  color: var(--dark);
}

.navbar-vertical .navbar-nav > .nav-item .nav-link.active {
  background: var(--primary-color-light) !important;
}

.nav-link.nav-link-danger,
.nav-link.nav-link-danger .nav-link-text,
.nav-link.nav-link-danger :deep(.nav-icon) {
  color: #dc3545 !important;
  font-weight: 700;
}

.nav-link.nav-link-primary-light,
.nav-link.nav-link-primary-light .nav-link-text,
.nav-link.nav-link-primary-light :deep(.nav-icon) {
  color: var(--primary-color-light) !important;
  font-weight: 700;
}

.nav-link-lock {
  margin-left: auto;
  padding-left: 0.4rem;
  font-size: 0.7rem;
  opacity: 0.6;
}

.nav-item-sub {
  padding-left: 0.75rem;
}

.nav-link-sub {
  font-size: 0.8rem !important;
  opacity: 0.82;
  padding-top: 0.35rem !important;
  padding-bottom: 0.35rem !important;
  min-height: 2.1rem !important;
}

.nav-link-sub :deep(.nav-icon-wrapper) {
  transform: scale(0.82);
  transform-origin: center;
}

.nav-link-sub.active {
  opacity: 1;
}

.nav-dot {
  margin-left: auto;
}

.nav-link {
  overflow: hidden;
}

.nav-link.is-collapsed {
  justify-content: center;
  padding-left: 0.35rem !important;
  padding-right: 0.35rem !important;
}

.nav-link.is-collapsed :deep(.nav-icon-wrapper) {
  width: auto;
  min-width: 24px;
  margin: 0 !important;
  display: inline-flex;
  justify-content: center;
}

.nav-link.is-collapsed .nav-link-text,
.nav-link.is-collapsed .nav-dot,
.nav-link.is-collapsed :deep(.badge) {
  display: none !important;
}
</style>
